import { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Documentation, DocumentationFilterInputObject } from 'graphql-common';
import Modal from '@lib/components/Modal/Modal';
import ActionsRow from '@lib/components/ActionsRow/ActionsRow';
import { ModalMode } from '@lib/components/Modal/enums';
import Table from '@lib/components/Table/Table';
import {
  GetUrlParams,
  ORDERING_DIRECTION_ASC,
  OrderingParams,
  PAGINATION_PARAMS,
  PaginationParams,
} from '@lib/enums/urls';
import { APP_URLS } from 'constants/urls';
import useAiIndexingDocumentationsQueryHook from 'utils/fetch/hooks/useAiIndexingDocumentationsQueryHook';
import getIndexingDocumentsTableColumns from '../utils/getIndexingDocumentsTableColumns';

// The shared ORDERING_PARAMS default to `createdAt`, which no column here shows
// — the sort indicator would then point at nothing. Neither indexing axis is
// sortable server-side, so name is the only real choice.
const DEFAULT_ORDERING: OrderingParams = {
  [GetUrlParams.OrderingField]: 'name',
  [GetUrlParams.OrderingDirection]: ORDERING_DIRECTION_ASC,
};

interface TableProps {
  filters: DocumentationFilterInputObject[];
  includeReasonColumn?: boolean;
}

function IndexingDocumentsTable({ filters, includeReasonColumn }: TableProps) {
  const { t } = useTranslation();
  // Kept in local state rather than the URL: the modal sits on the Company
  // plan settings page and must not rewrite its query string.
  const paginationState = useState<PaginationParams>(PAGINATION_PARAMS);
  const orderingState = useState<OrderingParams>(DEFAULT_ORDERING);
  const [paginationParams] = paginationState;
  const [orderingParams] = orderingState;

  const { collection, loading, totalCount } =
    useAiIndexingDocumentationsQueryHook({
      filters,
      orderingParams,
      paginationParams,
    });

  // A new tab, not a navigation: this modal sits on the Company plan page and
  // navigating in place would tear it down.
  const onOpen = useCallback((item: Documentation) => {
    const to = APP_URLS.dashboard.documentation.view.getDynamicPath({
      pathParts: { id: String(item.id) },
    });
    window.open(to, '_blank')?.focus();
  }, []);

  const columns = useMemo(
    () => getIndexingDocumentsTableColumns({ onOpen, t, includeReasonColumn }),
    [onOpen, t, includeReasonColumn],
  );

  return (
    <Table
      columns={columns}
      // `documentations` rows are already BaseItems, so no reshaping step.
      data={collection}
      loading={loading}
      orderingState={orderingState}
      paginationState={paginationState}
      totalEntries={totalCount}
    />
  );
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description: string;
  filters: DocumentationFilterInputObject[];
  includeReasonColumn?: boolean;
}

/**
 * Read-only counterpart to `ParkedDocumentsModal` for the card's other two
 * non-searchable counts — "Not indexable" (SKIPPED) and "Failed".
 *
 * No retry action here, unlike the parked modal: SKIPPED documents processed
 * successfully and simply have no searchable text, so retrying fixes nothing;
 * FAILED is broader than the parked retry lane and includes documents
 * automatic retries haven't given up on yet, so a blanket retry button would
 * misrepresent what's actually stuck.
 *
 * **Full admin on the AI plan only** — same gating as `ParkedDocumentsModal`,
 * see `useAiIndexingDocumentationsQueryHook`.
 */
function IndexingDocumentsModal({
  isOpen,
  onClose,
  title,
  description,
  filters,
  includeReasonColumn,
}: Props) {
  const { t } = useTranslation();
  return (
    <Modal
      isOpen={isOpen}
      mode={ModalMode.FullScreen}
      onClose={onClose}
      title={title}
      description={description}
    >
      {isOpen ? (
        <IndexingDocumentsTable
          filters={filters}
          includeReasonColumn={includeReasonColumn}
        />
      ) : (
        <div />
      )}
    </Modal>
  );
}

export default memo(IndexingDocumentsModal);
